import { html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_DEVICE_CONTROL_RULES_CONTAINER } from '../../../../../../../../webcomponents.constants';
import { DeviceControlRuleItem, DeviceControlStatus } from '@nirvana/shared/dtos/types/policies/common';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { InputWidthValues } from '../../../../../../../../ui/gzInput';
import { GzFormFieldWebComponent } from 'apps/webcomponents/src/ui/gzForm/formField';
import {
  getPermissionTypesByDeviceClass,
  mapToDeviceControlRuleItem,
} from '../../../../../utils/uiSettings/deviceControl/device-control.values';
import { GzDropdownWebComponent } from '../../../../../../../../ui/gzDropdown';
import { GzInputWebComponent } from '../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { FormGroup } from '../../../../../../../../ui/gzForm/formGroup/form-group';
import { MaxLengthValidator } from '../../../../../../../../ui/gzForm/validators/max-length.validator';
import _ from 'lodash';
import { WebComponentEvent } from '../../../../../../../../framework/BaseWebComponentController';
import { Columns, columns, getRows } from '../gridComponents/grid-config';
import { GzSelectableTableRowGroupWebComponent } from '../../../../../../../../ui/gzSelectableTableRowGroup';
import { toggleVisibility } from '../../../../../../../../utils/htmlElement.utils';
import { DeviceType, DeviceControlRuleForm } from '../../../../../utils/uiSettings/deviceControl/device-control.types';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import { RequiredValidator } from '../../../../../../../../ui/gzForm/validators/required.validator';
@customElement(WEB_COMPONENT_DEVICE_CONTROL_RULES_CONTAINER)
export class GzRuleContainerWebComponent extends GzPolicyFullPageWindowFormWebComponent<DeviceControlRuleForm> {
  @property({ type: Object, attribute: true, reflect: true })
  private selectedDeviceControlRuleForm: DeviceControlRuleForm;

  @property({ type: Object, attribute: true, reflect: true })
  private readonly deviceControlRuleItem: DeviceControlRuleItem;

  constructor(selectedDeviceControlRuleForm: DeviceControlRuleForm, deviceControlRuleItem: DeviceControlRuleItem) {
    super();
    this.selectedDeviceControlRuleForm = selectedDeviceControlRuleForm;
    this.deviceControlRuleItem = deviceControlRuleItem;
  }

  @query('#permission-form-field')
  private permissionFormField: GzFormFieldWebComponent<GzDropdownWebComponent>;

  @query('#description-form-field')
  private descriptionFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#selection-group-form-field')
  private selectionGroupFormField: GzFormFieldWebComponent<GzSelectableTableRowGroupWebComponent<DeviceType, Columns>>;

  static get styles() {
    return styles;
  }

  protected setFormGroupValue(): void {
    this.formGroup.value = {
      ruleName: this.selectedDeviceControlRuleForm.ruleName,
      generalPermissions: this.selectedDeviceControlRuleForm.generalPermissions,
      permissions: this.selectedDeviceControlRuleForm.permissions,
    };
  }

  protected getConvertedFormValue(): DeviceControlRuleItem {
    const formValue = this.formGroup.value as unknown;
    return mapToDeviceControlRuleItem(formValue as DeviceControlRuleForm, this.deviceControlRuleItem);
  }

  protected createFormGroup() {
    this.descriptionFormField.Validators = [
      MaxLengthValidator(
        80,
        translateFn('The description must have a maximum of {maxLength} characters.', {
          maxLength: 80,
        })
      ),
      RequiredValidator(translateFn('Required field')),
    ];

    this.formGroup = FormGroup.build({
      ruleName: this.descriptionFormField,
      generalPermissions: this.permissionFormField,
      permissions: this.selectionGroupFormField,
    });
  }

  public async onDropDownChange(event: WebComponentEvent) {
    const value = _.cloneDeep(this.selectedDeviceControlRuleForm);
    value.generalPermissions = event.detail.value;
    this.selectedDeviceControlRuleForm = value;
    toggleVisibility(
      this.selectionGroupFormField,
      this.selectedDeviceControlRuleForm.generalPermissions === DeviceControlStatus.CUSTOM
    );
  }

  protected renderContent() {
    return html` <gz-form-field
        class="dropdown-from-field label-mw-250"
        id="permission-form-field"
        label="${translateFn('Permission')}"
        required
      >
        <gz-dropdown
          id="permission-dropdown"
          gz-form-input
          labelWidth="${InputWidthValues.LARGE}"
          .options="${getPermissionTypesByDeviceClass(this.selectedDeviceControlRuleForm.deviceClass)}"
          @selectionChanged="${this.onDropDownChange}"
        >
        </gz-dropdown>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <gz-form-field
        class="label-mw-250 description-form-field"
        id="description-form-field"
        label="${translateFn('Description')}"
        required
      >
        <gz-input id="description-input" inputWidth="${InputWidthValues.LARGE}" gz-form-input></gz-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <gz-form-field class="selection-group-form-field" id="selection-group-form-field" class="hidden">
        <gz-title fontSize="11" fontWeight="600" class="form-subtitle">${translateFn('custom permissions')}</gz-title>
        <gz-selectable-table-row-group
          gz-form-input
          allowFullColumnSelection
          .columns="${columns()}"
          .rows="${getRows(
            this.selectedDeviceControlRuleForm.permissions,
            this.selectedDeviceControlRuleForm.deviceClass
          )}"
        ></gz-selectable-table-row-group>
      </gz-form-field>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_DEVICE_CONTROL_RULES_CONTAINER: GzRuleContainerWebComponent;
  }
}
